// traceability_mesh — dark claim panel left (450px), the live register mesh right (v15
// slide 9): requirement refs grouped by category, deliverable rows with the gold acceptance
// rail at x1230, and the requirement→deliverable edges as straight thin lines (highlight
// edges gold). Edge endpoints are computed from the same row layout the shapes use, so the
// lines always meet their refs.
import type { MeshBound, TraceabilityMeshContent } from '../../../../shared/deckTypes.js';
import { Abs, Brand, Dkp, PageNo, fmtDotShort } from './chrome.js';

const REQ_X = 512;
const REQ_PITCH = 11.4;
const GROUP_HEAD_H = 17;
const GROUP_GAP = 19.5;
const DEL_X = 814;
const DEL_Y0 = 105;
const DEL_PITCH = 30.5;

export default function TraceabilityMeshPreview({ content, bound, pageNo }: {
  content: TraceabilityMeshContent;
  bound?: MeshBound;
  pageNo?: number | null;
}) {
  const c = content;
  const groups = bound?.groups ?? [];
  const dels = bound?.dels ?? [];
  const edges = bound?.edges ?? [];

  // lay the requirement column out once — the edge overlay reads the same positions
  const reqPos: Record<string, number> = {};
  let y = 102;
  const laidGroups = groups.map((g) => {
    const headerY = y;
    y += GROUP_HEAD_H;
    const refs = g.reqRefs.map((ref) => {
      const ry = y;
      reqPos[ref] = ry;
      y += REQ_PITCH;
      return { ref, y: ry };
    });
    y += GROUP_GAP;
    return { label: g.label, headerY, refs };
  });

  const delPos: Record<string, number> = {};
  dels.forEach((d, i) => { delPos[d.ref] = DEL_Y0 + i * DEL_PITCH; });
  const railH = dels.length > 0 ? DEL_Y0 + (dels.length - 1) * DEL_PITCH + 16 - 98 : 0;

  const highlighted = new Set(edges.filter((e) => e.highlight).map((e) => e.reqRef));
  const nReq = groups.reduce((n, g) => n + g.reqRefs.length, 0);

  return (
    <Dkp>
      {/* the dark claim panel */}
      <Abs x={0} y={0} w={450} h={720} style={{ background: '#0B0C0E', color: '#F3F1EC' }}>
        <div className="dkp-abs" style={{ left: 64, top: 56 }}>
          <span className="dkp-eyebrow" style={{ color: '#9A9DA3' }}>{c.eyebrow ?? ''}</span>
        </div>
        <div className="dkp-abs" style={{ left: 64, top: 96, width: 322 }}>
          <div className="dkp-serif" style={{ fontSize: 30.7, lineHeight: 1.22 }}>{c.title}</div>
        </div>
        <div className="dkp-abs" style={{
          left: 64, top: 300, width: 310, display: 'flex', flexDirection: 'column', gap: 22,
        }}>
          {(c.claims ?? []).map((claim, i) => (
            <div key={i} style={{ fontSize: 13.3, fontWeight: 500, color: '#D9D6CF', lineHeight: 1.5 }}>
              {claim}
            </div>
          ))}
        </div>
      </Abs>

      {/* column heads */}
      <Abs x={494} y={64}><span className="dkp-th">ANFORDERUNGEN · {nReq}</span></Abs>
      <Abs x={806} y={64}><span className="dkp-th">ARBEITSERGEBNISSE · {dels.length}</span></Abs>
      <Abs x={1150} y={64} w={66} style={{ textAlign: 'right' }}><span className="dkp-th">ABNAHME</span></Abs>

      {/* the edges — the master's bezier curves, highlight gold, the rest at 10% ink */}
      <Abs x={450} y={0} w={830} h={720} style={{ pointerEvents: 'none' }}>
        <svg width={830} height={720} viewBox="0 0 830 720" style={{ display: 'block' }}>
          {edges.map((e, i) => {
            const ry = reqPos[e.reqRef];
            const dy = delPos[e.delRef];
            if (ry === undefined || dy === undefined) return null;
            const x1 = REQ_X + 37 - 450; const y1 = ry + 4.8;
            const x2 = DEL_X - 8 - 450; const y2 = dy + 5.6;
            const mx = (x1 + x2) / 2;
            return (
              <path
                key={i}
                d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                fill="none"
                stroke={e.highlight ? '#C8A96E' : '#121316'}
                strokeOpacity={e.highlight ? 1 : 0.1}
                strokeWidth={e.highlight ? 1.6 : 0.8}
              />
            );
          })}
        </svg>
      </Abs>

      {/* requirement groups */}
      {laidGroups.map((g, gi) => (
        <div key={gi}>
          <Abs x={494} y={g.headerY} w={200}>
            <span className="dkp-mono" style={{
              fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: '#A39A87',
            }}>{g.label}</span>
          </Abs>
          {g.refs.map((r) => (
            <Abs key={r.ref} x={REQ_X} y={r.y} w={60}>
              <span className="dkp-mono" style={{
                fontSize: 8,
                fontWeight: highlighted.has(r.ref) ? 700 : 500,
                color: highlighted.has(r.ref) ? '#8A6F3E' : '#4B4E55',
              }}>{r.ref}</span>
            </Abs>
          ))}
        </div>
      ))}

      {/* deliverable rows + the gold acceptance rail */}
      {railH > 0 ? (
        <Abs x={1229.5} y={98} w={1} h={railH} style={{ background: '#C8A96E' }} />
      ) : null}
      {dels.map((d) => {
        const dy = delPos[d.ref];
        return (
          <div key={d.ref}>
            <Abs x={DEL_X} y={dy} w={40}>
              <span className="dkp-mono" style={{ fontSize: 9.3, fontWeight: 700, color: '#C8A96E' }}>{d.ref}</span>
            </Abs>
            <Abs x={858} y={dy + 2} w={240}>
              <span style={{
                fontSize: 12, fontWeight: 500, color: '#26282C', whiteSpace: 'nowrap',
                display: 'block', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{d.name}</span>
            </Abs>
            <Abs x={1164} y={dy + 3} w={40}>
              <span className="dkp-mono" style={{ fontSize: 8, color: '#6B6E75' }}>{fmtDotShort(d.due)}</span>
            </Abs>
            <Abs x={1046} y={dy + 5.7} w={120} h={0.8} style={{ background: '#DAD7D0' }} />
            <Abs x={1226.8} y={dy + 2.8} w={6.3} h={6.3} style={{ background: '#C8A96E', borderRadius: '50%' }} />
          </div>
        );
      })}

      {c.legend ? (
        <Abs x={806} y={674} w={410}>
          <span className="dkp-mono" style={{ fontSize: 8, letterSpacing: '.06em', color: '#6B6E75' }}>
            {c.legend}
          </span>
        </Abs>
      ) : null}

      {/* the brand sits on the dark panel — light lockup */}
      <Brand light />
      <PageNo n={pageNo} />
    </Dkp>
  );
}
